import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FirebaseCodeErrorService {
// Stream para mensajes de éxito (compatible con | async en plantillas)
  private _msgSuccess$ = new BehaviorSubject<string>('');
  readonly msgSuccess$ = this._msgSuccess$.asObservable();

  /** Emite un mensaje de éxito (opcionalmente se limpia solo). */
  showSuccess(msg: string, autoClearMs = 0): void {
    this._msgSuccess$.next(msg);
    if (autoClearMs > 0) {
      setTimeout(() => this.clearSuccess(), autoClearMs);
    }
  }

  /** Limpia el mensaje de éxito. */
  clearSuccess(): void {
    this._msgSuccess$.next('');
  }

  /** Traduce códigos de error de Firebase Auth a mensajes de UI. */
  codeError(code?: string): string {
    // Códigos frecuentes (Firebase v9+)
    const map: Record<string, string> = {
      'auth/invalid-credential': 'Los datos ingresados son incorrectos',
      'auth/invalid-email': 'El email es inválido',
      'auth/user-not-found': 'Usuario no encontrado',
      'auth/wrong-password': 'Los datos ingresados son incorrectos',
      'auth/email-already-in-use': 'El usuario ya existe',
      'auth/weak-password': 'La clave ingresada es muy débil',
      'auth/missing-email': 'Debe ingresar un email válido',
      'auth/popup-closed-by-user': 'Ventana cerrada por el usuario',
      'auth/user-disabled': 'Usuario inactivo',
      'auth/requires-recent-login': 'La actualización de la contraseña de un usuario es una operación de seguridad que requiere que el usuario haya accedido recientemente',
      // Alias / variantes que a veces aparecen:
      'auth/invalid-login-credentials': 'Los datos ingresados son incorrectos',
      'auth/network-request-failed': 'Error de red. Verificá tu conexión e intentá nuevamente.',
      // Personalizados de tu app (si los usás):
      'auth/inactive-user': 'Usuario inactivo',
      'auth/tokenbackend-invalid': 'Los datos ingresados son incorrectos',
      // Identity Toolkit / REST (lo que te está llegando)
      'EMAIL_EXISTS': 'El usuario ya existe',
      'INVALID_PASSWORD': 'Los datos ingresados son incorrectos',
      'INVALID_LOGIN_CREDENTIALS': 'Los datos ingresados son incorrectos',
      'EMAIL_NOT_FOUND': 'Usuario no encontrado',
      'USER_DISABLED': 'Usuario inactivo',
      'TOO_MANY_ATTEMPTS_TRY_LATER': 'Demasiados intentos. Probá más tarde.',
      'OPERATION_NOT_ALLOWED': 'Operación no permitida. Contactá soporte.',
      'UnknownHostException': 'Error de red. Verificá tu conexión e intentá nuevamente.',
      'NSURLErrorDomain': 'Error de red. Verificá tu conexión e intentá nuevamente.',
    };

    const key = code ?? '';
    return map[key] ?? `Error desconocido (${key || 'sin código'})`;
  }

  extractCode(err: any): string {
    // Caso Firebase JS SDK
    if (typeof err?.code === 'string' && err.code) return err.code;

    // Caso Capacitor plugin: Error.message = JSON string
    const msg = err?.message;
    if (typeof msg === 'string') {
      // Si es un JSON
      if (msg.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(msg);
          const code = parsed?.error?.message;
          if (typeof code === 'string' && code) return code; // <-- EMAIL_EXISTS
        } catch {}
      }

      // Si viene como “Firebase: Error (auth/...)”
      const m = msg.match(/auth\/[a-z0-9-]+/i);
      if (m?.[0]) return m[0];
    }

    // Caso backend Angular (si aplica)
    if (typeof err?.error?.code === 'string') return err.error.code;
    if (typeof err?.error?.message === 'string') return err.error.message;
    if (typeof err?.error?.error?.message === 'string') return err.error.error.message;

    return '';
  }
}